class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this._closedByUser = false; // Bug 2 fix: track user dismissal

    this.addEventListener('keyup', (evt) => {
      if (evt.code === 'Escape') {
        this._closedByUser = true;
        this.close();
      }
    });

    const overlay = this.querySelector('#CartDrawer-Overlay');
    overlay.addEventListener('click', this.close.bind(this));
    overlay.addEventListener('touchend', (evt) => {
      evt.preventDefault(); // Bug 1 fix: stop browser synthesising a click ~300ms later
      this._closedByUser = true;
      this.close();
    });

    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    this._closedByUser = false; // Bug 2 fix: reset flag on intentional open
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.style.display = 'block';

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.classList.add('animate', 'active');
      });
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this._closedByUser = true; // Bug 2 fix: mark that the user dismissed the drawer
    this.classList.remove('active', 'animate');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');

    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.style.display = 'none';
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      const newOverlay = overlay.cloneNode(true);
      overlay.parentNode.replaceChild(newOverlay, overlay);
      newOverlay.addEventListener('click', this.close.bind(this));
      newOverlay.addEventListener('touchend', (evt) => {
        evt.preventDefault(); // Bug 1 fix: repeated here for the cloned overlay
        this._closedByUser = true;
        this.close();
      });

      if (!this._closedByUser) { // Bug 2 fix: only open if user hasn't already dismissed it
        this.open();
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

function sdClampCartUpsellQuantity(input, value) {
  const min = Number(input.getAttribute('min') || 1);
  const max = Number(input.getAttribute('max') || 99);
  const nextValue = Math.min(max, Math.max(min, Number(value) || min));
  input.value = String(nextValue);
  return nextValue;
}

function sdFormatCartUpsellMoney(cents, currencyCode, fallback) {
  const amount = Number(cents);
  if (!Number.isFinite(amount)) return fallback || '';

  if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
    return window.Shopify.formatMoney(amount);
  }

  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
      style: 'currency',
      currency: currencyCode || 'GBP',
    }).format(amount / 100);
  } catch (_error) {
    return fallback || `£${(amount / 100).toFixed(2)}`;
  }
}

function sdCartUpsellLabel(label, quantity) {
  if (quantity <= 1) return label;

  const pluralLabel = label.replace(/\bticket\b/i, 'tickets');
  if (/^add\b/i.test(pluralLabel)) {
    return pluralLabel.replace(/^add\b/i, `Add ${quantity}`);
  }

  return `${pluralLabel} x ${quantity}`;
}

function sdUpdateCartUpsellButton(root) {
  if (!root) return;

  const input = root.querySelector('[data-sd-cart-upsell-quantity]');
  const button = root.querySelector('[data-sd-cart-upsell-add][data-unit-price]');
  if (!input || !button) return;

  const quantity = sdClampCartUpsellQuantity(input, input.value);
  const label = button.dataset.productAddLabel || 'Add ticket';
  const labelNode = button.querySelector('[data-sd-cart-upsell-add-text]');
  const totalNode = button.querySelector('[data-sd-cart-upsell-total]');
  const unitPrice = Number(button.dataset.unitPrice || 0);

  if (labelNode) {
    labelNode.textContent = sdCartUpsellLabel(label, quantity);
  }

  if (totalNode && unitPrice) {
    totalNode.textContent = sdFormatCartUpsellMoney(unitPrice * quantity, button.dataset.currencyCode, button.dataset.productPrice);
  }
}

document.addEventListener('click', (event) => {
  const qtyDecrease = event.target.closest('[data-sd-cart-upsell-qty-decrease]');
  const qtyIncrease = event.target.closest('[data-sd-cart-upsell-qty-increase]');

  if (qtyDecrease || qtyIncrease) {
    event.preventDefault();
    const control = event.target.closest('[data-sd-cart-upsell-quantity-control]');
    const input = control && control.querySelector('[data-sd-cart-upsell-quantity]');
    if (!input) return;

    sdClampCartUpsellQuantity(input, Number(input.value || 1) + (qtyIncrease ? 1 : -1));
    sdUpdateCartUpsellButton(input.closest('[data-cocktail-club-prompt]'));
    return;
  }

  const button = event.target.closest('[data-sd-cart-upsell-add]');
  if (!button) return;

  event.preventDefault();

  const variantId = button.dataset.variantId;
  const cartDrawer = document.querySelector('cart-drawer');
  if (!variantId || !cartDrawer) return;
  const promptRoot = button.closest('[data-cocktail-club-prompt]');
  const quantityInput = promptRoot && promptRoot.querySelector('[data-sd-cart-upsell-quantity]');
  const quantity = quantityInput ? sdClampCartUpsellQuantity(quantityInput, quantityInput.value) : 1;

  button.disabled = true;
  button.classList.add('is-loading');
  const originalHtml = button.innerHTML;
  button.textContent = 'ADDING';

  const body = JSON.stringify({
    id: Number(variantId),
    quantity,
    sections: cartDrawer.getSectionsToRender().map((section) => section.id),
    sections_url: window.location.pathname,
  });

  fetch(routes.cart_add_url, { ...fetchConfig('javascript'), body })
    .then((response) => response.json())
    .then((parsedState) => {
      if (parsedState.status) {
        button.innerHTML = originalHtml;
        sdUpdateCartUpsellButton(promptRoot);
        return;
      }

      cartDrawer.classList.remove('is-empty');
      cartDrawer.renderContents(parsedState);
    })
    .catch(() => {
      button.innerHTML = originalHtml;
      sdUpdateCartUpsellButton(promptRoot);
    })
    .finally(() => {
      button.classList.remove('is-loading');
      button.disabled = false;
    });
});

document.addEventListener('change', (event) => {
  const input = event.target.closest('[data-sd-cart-upsell-quantity]');
  if (!input) return;

  sdClampCartUpsellQuantity(input, input.value);
  sdUpdateCartUpsellButton(input.closest('[data-cocktail-club-prompt]'));
});

document.addEventListener('input', (event) => {
  const input = event.target.closest('[data-sd-cart-upsell-quantity]');
  if (!input) return;

  sdUpdateCartUpsellButton(input.closest('[data-cocktail-club-prompt]'));
});

(function () {
  const FREE_GIFT_PROPERTY = '_sd_free_gift';
  let syncing = false;
  let syncTimer = null;

  function freeGiftConfig() {
    const el = document.querySelector('[data-sd-free-gift-config]');
    if (!el) return null;

    return {
      enabled: el.dataset.enabled === 'true',
      valid: el.dataset.valid === 'true',
      variantId: Number(el.dataset.variantId || 0),
      threshold: Number(el.dataset.threshold || 0),
    };
  }

  function isFreeGiftItem(item) {
    return item && item.properties && item.properties[FREE_GIFT_PROPERTY] === 'true';
  }

  function drawerSections() {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      return drawer.getSectionsToRender().map((section) => section.id);
    }

    return ['cart-drawer', 'cart-icon-bubble'];
  }

  function renderCartSections(parsedState) {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && parsedState && parsedState.sections && typeof drawer.renderContents === 'function') {
      drawer.classList.toggle('is-empty', parsedState.item_count === 0);
      drawer.renderContents(parsedState);
      return;
    }

    const drawerItems = document.querySelector('cart-drawer-items');
    if (drawerItems && typeof drawerItems.onCartUpdate === 'function') {
      drawerItems.onCartUpdate();
    }
  }

  function updateGiftLines(updates) {
    return fetch(routes.cart_update_url, {
      ...fetchConfig('javascript'),
      body: JSON.stringify({
        updates,
        sections: drawerSections(),
        sections_url: window.location.pathname,
      }),
    })
      .then((response) => response.json())
      .then((parsedState) => {
        renderCartSections(parsedState);
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'sd-free-gift', cartData: parsedState, variantId: null });
      });
  }

  function addGift(variantId) {
    return fetch(routes.cart_add_url, {
      ...fetchConfig('javascript'),
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        properties: {
          [FREE_GIFT_PROPERTY]: 'true',
        },
        sections: drawerSections(),
        sections_url: window.location.pathname,
      }),
    })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.status) return;

        renderCartSections(parsedState);
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'sd-free-gift', cartData: parsedState, variantId });
      });
  }

  function syncFreeGift() {
    if (syncing) return Promise.resolve();

    const config = freeGiftConfig();
    syncing = true;

    return fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cart) => {
        const giftItems = cart.items.filter(isFreeGiftItem);
        const qualifyingSubtotal = cart.items.reduce((total, item) => {
          return isFreeGiftItem(item) ? total : total + item.final_line_price;
        }, 0);

        const shouldRemoveAll = !config || !config.enabled || !config.valid || !config.variantId || qualifyingSubtotal < config.threshold;
        if (shouldRemoveAll) {
          if (!giftItems.length) return null;

          const updates = {};
          giftItems.forEach((item) => {
            updates[item.key] = 0;
          });
          return updateGiftLines(updates);
        }

        const matchingGift = giftItems.find((item) => Number(item.variant_id) === config.variantId);
        const updates = {};

        giftItems.forEach((item) => {
          if (Number(item.variant_id) !== config.variantId || item.key !== (matchingGift && matchingGift.key)) {
            updates[item.key] = 0;
          }
        });

        if (matchingGift && matchingGift.quantity !== 1) {
          updates[matchingGift.key] = 1;
        }

        if (Object.keys(updates).length) {
          return updateGiftLines(updates);
        }

        if (!matchingGift) {
          return addGift(config.variantId);
        }

        return null;
      })
      .catch((error) => {
        console.error('[Salford] Free gift sync failed:', error);
      })
      .finally(() => {
        syncing = false;
      });
  }

  function scheduleFreeGiftSync(delay = ON_CHANGE_DEBOUNCE_TIMER) {
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(syncFreeGift, delay);
  }

  document.addEventListener('DOMContentLoaded', () => scheduleFreeGiftSync(100));

  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    if (event && event.source === 'sd-free-gift') return;
    scheduleFreeGiftSync();
  });
})();

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
